import { useQuery, useMutation } from '@apollo/client/react';
import { useMelwaysInfo } from '@seek/melways-react';
import _orderBy from 'lodash/orderBy';

import { gql } from 'src/__generated__';
import { useApplicationProcess } from 'src/hooks/useApplicationProcess';
import { logError } from 'src/modules/error-logger/browserLog';
import { SET_DEFAULT_RESUME } from 'src/modules/graphql-client/mutations/setDefaultResume.graphql';
import { getOperationNameFromQuery } from 'src/modules/utils';

const DOCUMENTS_QUERY = gql(`
  query DocumentsQuery {
    viewer {
      _id
      resumes {
        ...resume
      }
    }
  }
`);

export function useDocuments() {
  const { data, loading, error } = useQuery(DOCUMENTS_QUERY);
  const applicationProcess = useApplicationProcess();
  const [setDefaultResumeMutation] = useMutation(SET_DEFAULT_RESUME);
  const { language } = useMelwaysInfo();

  const resumes = _orderBy(
    data?.viewer?.resumes || [],
    ['createdDateUtc'],
    ['desc'],
  );

  const resumesMap = new Map(resumes.map((item) => [item.id, item]));
  const defaultId = applicationProcess.data?.documents
    .lastAppliedResumeIdPrefill as string;
  const defaultResume = resumesMap.get(defaultId);
  const defaultCoverletter =
    applicationProcess.data?.documents.lastWrittenCoverLetter?.content;

  const shouldRenderselectionCriteria =
    applicationProcess.data?.documents.selectionCriteriaRequired;

  const setDefaultResume = async (resumeId: string) => {
    const resumesAfterDelete = resumes.filter(
      (r) => r.id !== resumeId && r.fileMetadata.virusScanStatus !== 'HasVirus',
    );
    const hasIsDefault = resumesAfterDelete.some((r) => r.isDefault);

    if (!hasIsDefault && resumesAfterDelete.length !== 0) {
      try {
        await setDefaultResumeMutation({
          variables: {
            resumeId: { id: resumesAfterDelete[0].id },
            languageCode: language,
          },
        });
      } catch (err) {
        logError(
          {
            type: 'set-default-resume-error',
            message: 'Error setting default resume',
          },
          {
            error: err,
            resumeId: resumesAfterDelete[0].id,
            operationName: getOperationNameFromQuery(SET_DEFAULT_RESUME),
          },
        );
      }
    }
  };

  return {
    resumes,
    defaultResume,
    defaultCoverletter,
    shouldRenderselectionCriteria,
    setDefaultResume,
    loading: loading || applicationProcess.loading,
    error: error || applicationProcess.error,
  };
}
